/**
 * Geste de la visionneuse : le doigt emmène la photo, et au relâchement on
 * choisit la précédente, la suivante, ou on revient.
 *
 * La décision ne regarde pas seulement l'endroit où le doigt s'arrête. Un flick
 * court doit tourner la page, et un geste qui repart en arrière doit pouvoir
 * annuler — d'où la projection de la vitesse, la même décélération qu'un
 * défilement, un cran plus vif (0,995) pour qu'un tremblement de fin de geste
 * ne fasse pas changer de photo.
 */

export const FLICK_DECELERATION = 0.995;

/** Distance, en pixels, que la vitesse ferait encore parcourir en ralentissant. */
export function projectFlick(velocityPxPerSec: number, deceleration = FLICK_DECELERATION): number {
  if (!Number.isFinite(velocityPxPerSec) || deceleration <= 0 || deceleration >= 1) return 0;
  return (velocityPxPerSec / 1000) * (deceleration / (1 - deceleration));
}

/**
 * Vitesse en px/s sur les derniers instants du geste.
 *
 * La dernière paire d'échantillons est trop bruitée : un doigt qui s'arrête
 * tremble de quelques pixels et la photo changerait toute seule. On remonte
 * d'environ 80 ms, assez pour lisser, assez court pour suivre un vrai flick.
 */
export function velocityFromSamples(samples: readonly { x: number; t: number }[]): number {
  if (samples.length < 2) return 0;
  const last = samples[samples.length - 1]!;
  let anchor = samples[0]!;
  for (let i = samples.length - 2; i >= 0; i--) {
    const sample = samples[i]!;
    anchor = sample;
    if (last.t - sample.t >= 80) break;
  }
  const dt = last.t - anchor.t;
  if (dt <= 0) return 0;
  return ((last.x - anchor.x) / dt) * 1000;
}

/**
 * `offsetPx` suit le doigt : négatif, on tire vers la photo suivante.
 * La vitesse est dans le même sens, en px/s.
 *
 * Retour : `1` suivante, `-1` précédente, `0` on reste sur place.
 */
export function decideSwipe(offsetPx: number, velocityPxPerSec: number, width: number): -1 | 0 | 1 {
  if (!(width > 0) || !Number.isFinite(offsetPx) || !Number.isFinite(velocityPxPerSec)) return 0;
  const projected = offsetPx + projectFlick(velocityPxPerSec);
  // 18 % de la largeur, borné : sur un grand téléphone le seuil ne s'éloigne
  // pas au point qu'il faille traverser l'écran, et sur un étroit un frôlement
  // ne suffit pas.
  const threshold = Math.min(Math.max(width * 0.18, 36), 96);
  if (projected <= -threshold) return 1;
  if (projected >= threshold) return -1;
  return 0;
}

/**
 * Ressort amorti critique. Pas de rebond : une photo qui dépasse son cadre
 * puis revient se lit comme une erreur, pas comme du poids.
 *
 * `responseSeconds` est le temps caractéristique pour rejoindre la cible, pas
 * une durée fixe — la vitesse du doigt au relâchement est reprise telle quelle,
 * pour qu'il n'y ait pas de couture entre le glissé et la fin du geste.
 */
export function criticalSpring(responseSeconds = 0.28): { stiffness: number; damping: number } {
  const omega = (2 * Math.PI) / responseSeconds;
  const stiffness = omega * omega;
  return { stiffness, damping: 2 * Math.sqrt(stiffness) };
}

export function springStep(
  x: number,
  v: number,
  target: number,
  dt: number,
  params: { stiffness: number; damping: number },
): { x: number; v: number } {
  const accel = -params.stiffness * (x - target) - params.damping * v;
  const nextV = v + accel * dt;
  return { x: x + nextV * dt, v: nextV };
}

/**
 * Anime `from` → `to` et peut être annulé à tout moment : un doigt qui
 * rattrape la photo en cours de route repart de là où elle est, pas de la
 * cible.
 */
export function animateLightboxSpring(
  from: number,
  to: number,
  velocityPxPerSec: number,
  onUpdate: (value: number) => void,
  onComplete: () => void,
): () => void {
  const params = criticalSpring();
  let x = from;
  let v = velocityPxPerSec;
  let last = performance.now();
  let raf = 0;
  let stopped = false;

  const frame = (now: number) => {
    if (stopped) return;
    let remaining = Math.min(0.048, (now - last) / 1000);
    last = now;
    if (remaining <= 0) remaining = 1 / 60;
    const step = 1 / 120;
    while (remaining > 0) {
      const dt = Math.min(step, remaining);
      const before = x;
      const next = springStep(x, v, to, dt, params);
      x = next.x;
      v = next.v;
      remaining -= dt;
      // On s'arrête en franchissant la cible, pas avant : un flick qui revient
      // doit encore pouvoir dépasser un peu puis se faire rattraper.
      const crossed = (before < to && x >= to) || (before > to && x <= to);
      const settled = Math.abs(to - x) < 0.5 && Math.abs(v) < 40;
      if (crossed || settled) {
        onUpdate(to);
        onComplete();
        return;
      }
    }
    onUpdate(x);
    raf = requestAnimationFrame(frame);
  };

  raf = requestAnimationFrame(frame);
  return () => {
    stopped = true;
    cancelAnimationFrame(raf);
  };
}
